import { IDLE_ACTIVITY_EVENTS } from "@/constants/auth-constant";
import { useEffect, useRef } from "react";

interface UseIdleLogoutProps {
  timeout?: number;
  onIdle: () => void;
  /** Only an authenticated screen should time out; auth and public pages pass false. */
  enabled?: boolean;
}

/**
 * @description Calls `onIdle` once the user has not interacted for `timeout`.
 *
 * @author [Rajkumar Rathod]
 */
export const useIdleLogout = ({
  timeout = 30 * 60 * 1000, //30 mn
  onIdle,
  enabled = true,
}: UseIdleLogoutProps) => {
  const timerRef = useRef<number | null>(null);
  // Read through a ref so a new callback identity does not restart the timer.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = window.setTimeout(() => {
        onIdleRef.current();
      }, timeout);
    };

    resetTimer();
    IDLE_ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, resetTimer)
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      IDLE_ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, resetTimer)
      );
    };
  }, [timeout, enabled]);
};
